/**
 * Строка бренда — 28 px (§4.3).
 *
 * Строка знает про себя ровно четыре вещи: выделена ли она, раскрыта ли,
 * есть ли в ней моя правка и ушла ли она из текущего фильтра (§5.4).
 * Приглушения строк нет вообще: видимость «Нет» стоит у 129 брендов из 230,
 * приглушить 56 % таблицы — убить читаемость (§0, §4.3).
 */
import { Link } from 'react-router-dom'
import type { Brand, ChannelCode, Conflict } from '@shared/types.ts'
import {
  Badge, Checkbox, IconArrowDown, IconChevronDown, IconChevronRight, IconWarning, Tooltip,
} from '../../../components/index.js'
import { brandCell, channelCell, type Overlay } from '../../../lib/draft-overlay.js'
import type { CellCursorApi } from '../editing/useCellCursor.js'
import { tableTexts as t } from '../texts.table.js'
import { CellBox, CellContent, cellLayer, NaCell, type CellActions } from './cells/index.js'
import type { ColumnDef, ColumnGroupCode } from './columns.js'
import { freshKey } from './fresh.js'
import { isApplicable, isInChannel, type TableRow } from './rows.js'
import { previousLabel, readCell, timeView, type ValueContext } from './values.js'
import './BrandRow.css'

/** Геометрия набора колонок: считается один раз на набор, не на строку. */
export interface TableLayout {
  columns: ColumnDef[]
  catalog: ColumnDef[]
  leftOffset: ReadonlyMap<string, number>
  rightOffset: ReadonlyMap<string, number>
  lastLeftCode: string | null
  firstRightCode: string | null
  totalWidth: number
}

/** Права на правку ячейки (§11.4): роль × поле × пустая/заполненная. */
export interface EditPermissions
{
  mayEdit: boolean
  /** «Пополняющий»: пустую ячейку править можно, заполненную — нет. */
  onlyEmpty: boolean
  reason: string
}

export interface RowActions extends CellActions {
  /** Выделение чекбоксом: Shift — диапазон, Ctrl/Cmd — добавить (§5.7). */
  toggleSelect: (brandId: number, modifiers: { shift: boolean; meta: boolean }) => void
  /** Протягивание по чекбоксам: зажали и повели. */
  dragSelectStart: (brandId: number) => void
  dragSelectOver: (brandId: number) => void
  /** Запись значения ячейки в черновик. */
  write: (column: ColumnDef, row: TableRow, value: unknown) => void
}

export interface BrandRowProps {
  row: Extract<TableRow, { kind: 'brand' }>
  rowIndex: number
  top: number
  height: number
  layout: TableLayout
  ctx: ValueContext
  overlay: Overlay
  cursor: CellCursorApi
  perms: EditPermissions
  conflicts: ReadonlyMap<number, Conflict>
  fresh: ReadonlySet<string>
  /** Число пустых полей зоны ответственности — колонка «Пропущено» (§4.9). */
  missingOf: (brand: Brand) => number
  selected: boolean
  expanded: boolean
  selectionEnabled: boolean
  /** Строка ушла из фильтра после моей правки (§5.4). */
  drift: boolean
  /** Новый бренд, показанный вне фильтра (§4.3). */
  outsideFilter: boolean
  /** Этот бренд открыт сейчас в карточке — строка подсвечена. */
  opened?: boolean
  hasDraft: boolean
  /** Правка спрятана в свёрнутой категории — точка перед именем (§4.3). */
  hiddenDraft: boolean
  actions: RowActions
  channelName: string
}

export function BrandRow(props: BrandRowProps) {
  const {
    row, rowIndex, top, height, layout, ctx, overlay, cursor, perms, conflicts, fresh,
    missingOf, selected, expanded, selectionEnabled, drift, outsideFilter, hasDraft,
    hiddenDraft, actions, channelName, opened,
  } = props

  const brand = row.brand
  const inChannel = isInChannel(brand, ctx.channel)
  const missing = missingOf(brand)

  // Бренд скрыт на сайте — строка выглядит выключенной, но остаётся рабочей.
  const hidden = channelCell(ctx.overlay, brand, ctx.channel, 'visibility').value === 'no'

  const classes = ['brand-row']
  if (hidden) classes.push('brand-row--hidden')
  if (selected) classes.push('brand-row--selected')
  if (hasDraft) classes.push('brand-row--draft')
  /* Открытый в карточке бренд виден и в таблице: иначе, закрыв карточку,
     человек ищет глазами, где он только что был. */
  if (opened) classes.push('brand-row--opened')

  return (
    <div
      className={classes.join(' ')}
      role="row"
      aria-rowindex={rowIndex + 1}
      aria-selected={selectionEnabled ? selected : undefined}
      aria-expanded={brand.categories.length > 0 ? expanded : undefined}
      style={{ top: `${top}px`, height: `${height}px`, width: `${layout.totalWidth}px` }}
    >
      {/* Фон и обводка карточки: липнут к левому краю видимой области, поэтому
          строка всегда выглядит скруглённой плашкой, а не обрезанной полосой.
          Обе стоят до ячеек — липкий элемент не может уехать левее своего места
          в потоке, а обводка всё равно поверх ячеек за счёт z-index. */}
      <span className="row-plate row-plate--bg" aria-hidden="true" />
      <span className="row-plate row-plate--frame" aria-hidden="true" />

      {layout.columns.map((column, colIndex) => {
        const geometry = {
          width: column.width,
          stickyLeft: layout.leftOffset.get(column.code),
          stickyRight: layout.rightOffset.get(column.code),
          edgeLeft: column.code === layout.lastLeftCode,
          edgeRight: column.code === layout.firstRightCode,
        }

        if (column.role === 'gutter') {
          return (
            <div
              key={column.code}
              className={geometry.edgeLeft ? 'cell cell--center cell--sticky-left gutter cell--edge-left' : 'cell cell--center cell--sticky-left gutter'}
              role="gridcell"
              aria-colindex={colIndex + 1}
              style={{ width: `${column.width}px`, left: `${geometry.stickyLeft ?? 0}px` }}
              onPointerEnter={() => actions.dragSelectOver(brand.id)}
            >
              {hasDraft || selected ? (
                <span className={selected ? 'gutter__stripe gutter__stripe--selected' : 'gutter__stripe'} aria-hidden="true" />
              ) : null}
              {/* Протягивание: зажать чекбокс и провести по строкам. Клик
                  по одной строке работает как раньше — жест начинается
                  только после входа курсора в соседнюю строку. */}
              {selectionEnabled ? (
                <span
                  className="gutter__check"
                  onPointerDown={(event) => {
                    if (event.button !== 0) return
                    actions.dragSelectStart(brand.id)
                  }}
                  onPointerEnter={() => actions.dragSelectOver(brand.id)}
                >
                  <Checkbox
                    checked={selected}
                    onChange={() => { /* выделение целиком в onClick: нужны модификаторы */ }}
                    label={<span className="visually-hidden">{t.row.select(brand.name)}</span>}
                    onClick={(event) => {
                      event.stopPropagation()
                      actions.toggleSelect(brand.id, { shift: event.shiftKey, meta: event.metaKey || event.ctrlKey })
                    }}
                  />
                </span>
              ) : null}
              {/* Шеврон есть всегда: бренд без категорий раскрывается в служебную
                  строку «категории не заданы · Добавить» (§4.3), а их 132. */}
              <button
                type="button"
                className="gutter__chevron"
                aria-label={expanded ? t.row.collapse(brand.name) : t.row.expand(brand.name)}
                onClick={(event) => { event.stopPropagation(); actions.expandRow() }}
              >
                {expanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
              </button>
              {drift ? (
                <Tooltip content={t.hint.willLeaveFilter}>
                  <span className="gutter__drift" aria-label={t.hint.willLeaveFilter}>
                    <IconArrowDown size={16} />
                  </span>
                </Tooltip>
              ) : null}
            </div>
          )
        }

        if (column.role === 'brand') {
          const name = brandCell<string>(overlay, brand, 'name')
          const unclear = brandCell<boolean>(overlay, brand, 'categoriesUnclear').value
          return (
            <div
              key={column.code}
              className={geometry.edgeLeft ? 'cell cell--sticky-left brand-cell cell--edge-left' : 'cell cell--sticky-left brand-cell'}
              role="gridcell"
              aria-colindex={colIndex + 1}
              style={{ width: `${column.width}px`, left: `${geometry.stickyLeft ?? 0}px` }}
            >
              {hiddenDraft ? (
                <Tooltip content={t.row.hasHiddenDraft}>
                  <span className="brand-cell__dot" aria-label={t.row.hasHiddenDraft} />
                </Tooltip>
              ) : null}
              <Link
                className="brand-cell__name t-brand t-truncate"
                to={`/brands/${brand.slug}?ch=${ctx.channel}`}
              >
                {name.value}
              </Link>
              {brand.status === 'archived' ? <Badge variant="neutral">{t.row.archived}</Badge> : null}
              {overlay.draftBrands.has(brand.id) ? (
                <Tooltip content={outsideFilter ? t.hint.newOutsideFilter : t.row.isNewLabel}>
                  <Badge variant="draft">{t.row.isNew}</Badge>
                </Tooltip>
              ) : null}
              {unclear ? (
                <Tooltip content={t.row.unclear}>
                  <span className="brand-cell__flag" aria-label={t.row.unclear}>
                    <IconWarning size={16} />
                  </span>
                </Tooltip>
              ) : null}
            </div>
          )
        }

        // Бренд не ведётся в канале — диагональная штриховка, курсор не ходит (П3).
        const channelColumn = column.source.kind === 'channel'
          || (column.source.kind === 'custom' && column.source.scope === 'brand_channel')
        if (!inChannel && channelColumn) {
          return (
            <CellBox
              key={column.code}
              column={column}
              {...geometry}
              layer="none"
              state="off-channel"
              active={false}
              editing={false}
              focusable={false}
              readOnly
              rowIndex={rowIndex}
              colIndex={colIndex}
              label={t.hint.notInChannel(brand.name, channelName)}
              tooltip={t.hint.notInChannel(brand.name, channelName)}
            >
              <span aria-hidden="true" />
            </CellBox>
          )
        }

        if (!isApplicable(column, row)) {
          return (
            <CellBox
              key={column.code}
              column={column}
              {...geometry}
              layer="none"
              state="na"
              active={false}
              editing={false}
              focusable={false}
              readOnly
              rowIndex={rowIndex}
              colIndex={colIndex}
              label={t.hint.naCell}
            >
              <NaCell />
            </CellBox>
          )
        }

        const cell = readCell(ctx, column, row)
        const conflict = cell?.change ? conflicts.get(cell.change.id) ?? null : null
        const isFresh = column.source.kind === 'channel'
          && fresh.has(freshKey(brand.id, ctx.channel, column.source.field))

        const editable = column.editor !== 'none' && perms.mayEdit
          && (!perms.onlyEmpty || (cell?.empty ?? true))
        const active = cursor.isActive(rowIndex, colIndex)
        const editing = cursor.isEditing(rowIndex, colIndex)

        const tooltip = cell?.isDraft
          ? t.hint.wasValue(previousLabel(ctx, column, cell), timeView(cell.change?.createdAt ?? null))
          : conflict
            ? t.edit.conflictCell(
              ctx.users.find((user) => user.id === conflict.changedBy)?.fullName ?? '—',
              conflict.currentLabel ?? '—',
              cell?.change?.newLabel ?? '—',
            )
            : column.editor !== 'none' && !editable
              ? perms.reason
              : undefined

        return (
          <CellBox
            key={column.code}
            column={column}
            {...geometry}
            layer={cellLayer(cell, conflict, isFresh)}
            state={cell?.empty ? 'empty' : 'value'}
            active={active}
            editing={editing}
            focusable={column.editor !== 'none'}
            readOnly={!editable}
            rowIndex={rowIndex}
            colIndex={colIndex}
            label={t.edit.cellLabel(column.srTitle ?? column.title, brand.name)}
            tooltip={tooltip}
            onActivate={() => cursor.focusCell({ row: rowIndex, col: colIndex })}
            onOpen={() => { if (editable) cursor.startEdit({ row: rowIndex, col: colIndex }) }}
          >
            {cell ? (
              <CellContent
                column={column}
                row={row}
                cell={cell}
                ctx={ctx}
                catalog={layout.catalog}
                active={active}
                editing={editing}
                readOnly={!editable}
                seed={editing ? cursor.seed : null}
                missing={missing}
                actions={actions}
                write={(value) => actions.write(column, row, value)}
                stepAfterCommit={(direction) => cursor.commitAndStep(direction, true)}
                commitAndRight={cursor.commitAndRight}
                openEditor={() => cursor.startEdit({ row: rowIndex, col: colIndex })}
                closeEditor={cursor.stopEdit}
              />
            ) : null}
          </CellBox>
        )
      })}

    </div>
  )
}

/** Группа колонок, которую разворачивает клик по индикатору. */
export type ExpandGroup = (group: ColumnGroupCode) => void
/** Канал, в который добавляет клик по зачёркнутой букве Р·О. */
export type AddToChannel = (channel: ChannelCode) => void
